import { Clock, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../utils';
import { Button } from '../../../core/button';
import { ExerciseFeedbackList } from '../shared/exercise-feedback-list';
import type { ExerciseFeedbackMessage } from '../../hooks/content-renderer-types';
import type {
  SessionWithTurns,
  CompletionResult,
  ConversationTurn,
  SkillAssessment,
} from './conversational-exercise-types';
import { formatDate } from './conversational-exercise-types';

interface SessionResultProps {
  /** Historical session data (when viewing past sessions) */
  session: SessionWithTurns | null;
  /** Completion result from just-finished conversation */
  result: CompletionResult | null;
  /** Live turns from the current conversation */
  sessionTurns: ConversationTurn[];
  /** Teacher feedback passed through from parent */
  feedback?: ExerciseFeedbackMessage[];
  /** Handler for navigating back from history view */
  onBackFromHistory?: () => void;
  /** Handler for starting a new conversation */
  onStartNew?: () => void;
  /** Whether the exercise is in read-only mode */
  isReadOnly?: boolean;
}

function getEvaluationTitle(score: number | undefined | null, maxScore: number): string {
  if (score === undefined || score === null) return 'Evaluation';
  const percentage = (score / maxScore) * 100;
  if (percentage >= 90) return 'Excellent Work!';
  if (percentage >= 70) return 'Great Job!';
  if (percentage >= 50) return 'Good Effort!';
  return 'Keep Practicing!';
}

function getEvaluationVariant(
  score: number | undefined | null,
  maxScore: number
): 'success' | 'warning' | 'destructive' | 'info' {
  if (score === undefined || score === null) return 'info';
  if (score >= maxScore * 0.7) return 'success';
  if (score >= maxScore * 0.5) return 'warning';
  return 'destructive';
}

export function SessionResult({
  session,
  result,
  sessionTurns,
  feedback,
  onBackFromHistory,
  onStartNew,
  isReadOnly,
}: SessionResultProps) {
  const { t } = useTranslation('p2e');
  const evaluation = session?.evaluation ?? (result ? {
    overallScore: result.score,
    maxScore: result.maxScore,
    overallFeedback: result.feedback,
    skillAssessments: result.skillAssessments,
    corrections: result.corrections,
    lowContent: result.lowContent,
    recovered: result.recovered,
  } : null);
  const corrections = evaluation?.corrections ?? [];

  const displayTurns = session?.turns?.map((t) => ({
    role: t.role as 'user' | 'assistant',
    content: t.content,
    timestamp: new Date(t.timestamp as string | Date),
  })) ?? sessionTurns;

  const score = session?.score ?? evaluation?.overallScore ?? result?.score;
  const maxScore = session?.maxScore ?? evaluation?.maxScore ?? result?.maxScore ?? 100;
  const feedbackText = evaluation?.overallFeedback ?? result?.feedback;
  const skillAssessments: SkillAssessment[] =
    evaluation?.skillAssessments ?? result?.skillAssessments ?? [];

  // Suppress unused variable lint — kept for future skill assessment UI
  void skillAssessments;

  const feedbackMessages: ExerciseFeedbackMessage[] = [];

  if (feedbackText) {
    feedbackMessages.push({
      source: 'ai',
      title: getEvaluationTitle(score, maxScore),
      description: feedbackText,
      variant: getEvaluationVariant(score, maxScore),
    });
  }

  const teacherFeedback = feedback?.find((f) => f.source === 'teacher');
  if (teacherFeedback) {
    feedbackMessages.push(teacherFeedback);
  }

  return (
    <div className="space-y-5">
      {/* Session metadata for history view */}
      {session && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>{formatDate(session.startedAt as string | Date)}</span>
          </div>
          {onBackFromHistory && (
            <Button
              onClick={onBackFromHistory}
              variant="link"
              className="gap-1 text-[#cf2e2e] p-0 h-auto"
            >
              <RotateCcw className="h-4 w-4" />
              Back
            </Button>
          )}
        </div>
      )}

      {(evaluation?.recovered || evaluation?.lowContent) && (
        <div className="rounded-[12px] border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
          {evaluation?.recovered ? t('zine.speak.recoveredNote') : t('zine.speak.lowContent')}
        </div>
      )}

      {/* Feedback using ExerciseFeedbackList */}
      {feedbackMessages.length > 0 && (
        <ExerciseFeedbackList feedback={feedbackMessages} />
      )}

      {/* Evidence-based corrections: her words → the corrected form */}
      {corrections.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-semibold text-gray-900">{t('zine.speak.correctionsTitle')}</h4>
          <ul className="m-0 list-none space-y-2 p-0">
            {corrections.map((correction, index) => (
              <li
                key={index}
                className="rounded-[12px] border border-rose-100 bg-white p-3 text-sm leading-relaxed text-gray-700"
              >
                <p className="m-0">
                  <span className="font-semibold text-gray-500">{t('zine.speak.correctionsYouSaid')}: </span>
                  <span className="text-[#cf2e2e] line-through decoration-[#cf2e2e]/60">{correction.studentQuote}</span>
                </p>
                <p className="m-0">
                  <span className="font-semibold text-gray-500">{t('zine.speak.correctionsSay')}: </span>
                  <span className="font-semibold text-gray-900">{correction.correctedForm}</span>
                  {correction.habit && (
                    <span className="ml-2 rounded-full bg-sky-50 px-2 py-0.5 text-xs text-sky-800">{correction.habit}</span>
                  )}
                </p>
                {correction.explanation && <p className="m-0 mt-1 text-xs text-gray-500">{correction.explanation}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Conversation history */}
      {displayTurns.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-semibold text-gray-900">Conversation History</h4>
          <div className="max-h-64 space-y-2 overflow-y-auto rounded-[16px] border border-rose-100 bg-rose-50/50 p-4">
            {displayTurns.map((turn, index) => (
              <div key={index} className="text-sm leading-relaxed">
                <span
                  className={cn(
                    'font-semibold',
                    turn.role === 'user' ? 'text-[#cf2e2e]' : 'text-gray-500'
                  )}
                >
                  {turn.role === 'user' ? 'You: ' : 'Tutor: '}
                </span>
                <span className="text-gray-700">{turn.content}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Start new conversation button */}
      {!isReadOnly && onStartNew && (
        <div className="flex justify-center pt-2">
          <Button
            onClick={onStartNew}
            variant="outline"
            className="gap-2 rounded-full px-6 py-3 border-2 border-[#cf2e2e] text-[#cf2e2e] hover:bg-rose-50 hover:-translate-y-0.5 hover:shadow-md transition-all"
          >
            <RotateCcw className="h-4 w-4" />
            Start New Conversation
          </Button>
        </div>
      )}
    </div>
  );
}
